/**
 * Dexie (IndexedDB wrapper) — Offline-first storage
 *
 * Maqsad:
 *   - Offline rejimda mahsulotlar va sotuvlarni mahalliy saqlash
 *   - Internet kelganda server bilan sinxronlash
 *
 * Tablolar:
 *   - products      — server'dan keshlangan mahsulotlar (delta sync)
 *   - pendingOps    — offline'da qilingan operatsiyalar (sync queue)
 *   - deadLetterQueue — 5 marta xato bergan operatsiyalar
 *   - syncMeta      — sync metadata (oxirgi sync vaqti, va h.k.)
 *
 * Schema versiyasi: 1
 *   Kelajak migration'lar: this.version(2).stores({...}) bilan qo'shiladi
 */

import type { DeadLetterEntry, PendingOperation, ProductDto, SyncStatus } from '@avtofix/shared';
import Dexie from 'dexie';
import type { Table } from 'dexie';

interface SyncMetaRecord {
  key: string;
  value: number;
}

class AvtoFixDB extends Dexie {
  products!: Table<ProductDto, string>;
  pendingOps!: Table<PendingOperation, string>;
  deadLetterQueue!: Table<DeadLetterEntry, string>;
  syncMeta!: Table<SyncMetaRecord, string>;

  constructor() {
    super('avtofix');

    this.version(1).stores({
      // Indexlar: id (primary), updatedAt (delta sync), name (qidiruv)
      products: 'id, updatedAt, name, sku, status, storeId',
      // Indexlar: id (primary), status (filter), createdAt (sort), resource (group)
      pendingOps: 'id, status, createdAt, resource, action',
      // Dead letter queue
      deadLetterQueue: 'id, originalId, movedAt, recoverable',
      // Sync metadata
      syncMeta: 'key',
    });
  }

  /**
   * Sync metadata olish
   */
  async getMeta(key: string): Promise<number | null> {
    const record = await this.syncMeta.get(key);
    return record?.value ?? null;
  }

  /**
   * Sync metadata o'rnatish
   */
  async setMeta(key: string, value: number): Promise<void> {
    await this.syncMeta.put({ key, value });
  }

  /**
   * Pending operatsiya qo'shish
   */
  async addPendingOp<T>(
    op: Omit<PendingOperation<T>, 'id' | 'attempts' | 'status' | 'createdAt'>,
  ): Promise<string> {
    const id = `offline_${String(Date.now())}_${Math.random().toString(36).slice(2, 9)}`;
    const fullOp: PendingOperation<T> = {
      ...op,
      id,
      status: 'pending' as SyncStatus,
      attempts: 0,
      createdAt: Date.now(),
    };
    await this.pendingOps.put(fullOp as PendingOperation);
    return id;
  }

  /**
   * Pending operatsiyani DLQ ga ko'chirish (5 marta xato bo'lgandan keyin)
   */
  async moveToDeadLetter(op: PendingOperation, error: string): Promise<void> {
    const dlqEntry: DeadLetterEntry = {
      id: `dlq_${op.id}`,
      originalId: op.id,
      resource: op.resource,
      action: op.action,
      data: op.data,
      error,
      attempts: op.attempts,
      movedAt: Date.now(),
      recoverable: true,
    };
    await this.transaction('rw', this.pendingOps, this.deadLetterQueue, async () => {
      await this.deadLetterQueue.put(dlqEntry);
      await this.pendingOps.delete(op.id);
    });
  }

  /**
   * Hamma narsani tozalash (logout uchun)
   */
  async clearAll(): Promise<void> {
    await this.transaction(
      'rw',
      this.products,
      this.pendingOps,
      this.deadLetterQueue,
      this.syncMeta,
      async () => {
        await this.products.clear();
        await this.pendingOps.clear();
        await this.deadLetterQueue.clear();
        await this.syncMeta.clear();
      },
    );
  }
}

// Singleton — butun ilova uchun bitta DB instance
export const db = new AvtoFixDB();

// Sync metadata key'lar (avoid magic strings)
export const SyncMetaKeys = {
  PRODUCTS_LAST_SYNC: 'products_last_sync',
  ORDERS_LAST_SYNC: 'orders_last_sync',
  CUSTOMERS_LAST_SYNC: 'customers_last_sync',
  LAST_FULL_SYNC: 'last_full_sync',
} as const;
